export function sanitizeUrl(url, validationOptions = {}) {
    if (!isValidUrl(url, validationOptions)) {
        return null;
    }

    try {
        const parsedUrl = new URL(url, window.location.origin);

        if (window.location.origin !== parsedUrl.origin) {
            return null;
        }

        parsedUrl.pathname = encodeURI(decodeURI(parsedUrl.pathname));

        if (url.startsWith('/')) {
            return parsedUrl.pathname + parsedUrl.search;
        } else {
            return parsedUrl.toString();
        }
    } catch (e) {
        console.error('Invalid URL:', e);
        return null;
    }
}

/**
 * True when a navigation target resolves to the current origin.
 * Rejects javascript:/data: schemes, protocol-relative and cross-origin URLs,
 * and strings the URL parser cannot read.
 */
export function isSameOriginUrl(url) {
    if (typeof url !== 'string') return false;

    try {
        return new URL(url, window.location.origin).origin === window.location.origin;
    } catch {
        return false;
    }
}

/**
 * Checks if a given URL string is valid and safe based on specified options.
 *
 * The function performs several checks:
 * - Ensures the input is a string.
 * - Optionally requires the URL to be relative (starts with '/').
 * - Decodes the URL and disallows protocol-relative URLs (e.g., //evil.com).
 * - Disallows URLs containing forbidden patterns such as javascript:, data:, vbscript:, script tags, angle brackets, and event handler attributes.
 *
 * @param {string} url - The URL string to validate.
 * @param {Object} [options={}] - Optional settings.
 * @param {boolean} [options.requireRelativeUrl=false] - If true, only allows relative URLs (must start with '/').
 * @returns {boolean} Returns true if the URL is valid and safe, otherwise false.
 */
export function isValidUrl(url, options = {}) {
    if (typeof url !== 'string') return false;
    if (options.requireRelativeUrl && !url.startsWith('/')) return false;

    // Decode URI component for additional checks
    let decodedUrl;
    try {
        decodedUrl = decodeURIComponent(url);
    } catch {
        decodedUrl = url;
    }

    // Disallow URLs that decode to a protocol-relative URL (e.g., //evil.com)
    // Disallow javascript/data/vbscript, suspicious patterns, and event handler attributes (case-insensitive)
    const forbiddenPatterns = [
        /^\/\//,
        /javascript:/i,
        /data:/i,
        /vbscript:/i,
        /<|>|script/i,
        /\bon\w+=/i, // matches onerror=, onclick=, etc.
    ];
    if (
        forbiddenPatterns.some((pattern) => pattern.test(url)) ||
        forbiddenPatterns.some((pattern) => pattern.test(decodedUrl))
    ) {
        return false;
    }

    return true;
}
